import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Delete, CheckCircle, Notifications, Info, Warning, Error } from '@mui/icons-material';
import { useNotifications } from '../hooks';
import { colors, transitions } from '../theme/theme';

export const NotificationsPage = () => {
  const { notifications, isLoading, error } = useNotifications({ page: 1, per_page: 50 });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle sx={{ color: colors.success.main }} />;
      case 'warning':
        return <Warning sx={{ color: colors.warning.main }} />;
      case 'error':
        return <Error sx={{ color: colors.error.main }} />;
      default:
        return <Info sx={{ color: colors.info.main }} />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMins < 1) return 'Just now';
    if (diffInMins < 60) return `${diffInMins} minute${diffInMins > 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <AdminLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load notifications. Please try again.
          </Alert>
        )}

        <Paper elevation={0} sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: colors.shadows.base,
        }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications?.data?.map((notification: any, index: number) => (
                <Box key={notification.id}>
                  <ListItem
                    sx={{
                      bgcolor: notification.read_at ? 'transparent' : `${colors.primary.main}08`,
                      '&:hover': {
                        bgcolor: colors.primary[50],
                        transition: transitions.fast,
                      },
                      py: 2,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{
                          color: colors.error.main,
                          '&:hover': {
                            bgcolor: `${colors.error.main}15`,
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: notification.read_at ? 400 : 600 }}>
                            {notification.title || notification.data?.title || 'Notification'}
                          </Typography>
                          {!notification.read_at && (
                            <Chip label="New" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {notification.message || notification.data?.message || 'No message'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < (notifications?.data?.length || 0) - 1 && <Divider />}
                </Box>
              ))}
              {(!notifications?.data || notifications.data.length === 0) && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                        <Notifications sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography color="text.secondary">No notifications</Typography>
                      </Box>
                    }
                  />
                </ListItem>
              )}
            </List>
          )}
        </Paper>
      </Box>
    </AdminLayout>
  );
};
